import React from 'react';
import { Card } from '@amzn/stencil-react-components/card';
import { Col } from '@amzn/stencil-react-components/layout';
import { BadgeType } from '@amzn/stencil-react-components/badge';
import { AuditStatus, AuditType, TaskStatus } from '@amzn/gbsa-helios-typescript-client';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

// ─── Status Mappings (exported for feature cards that need badge/label access) ─
//
// AuditStatus and TaskStatus share several string values (IN_PROGRESS, COMPLETED,
// CANCELLED). Duplicate computed keys are a TS error, so shared values appear once
// under the AuditStatus entry — the mapping applies to both audit and task cards.
// Task-only statuses (DRAFT, ASSIGNED, OVERDUE) are added separately below.

export const STATUS_BADGE_TYPE: Record<string, BadgeType> = {
  // Shared audit + task statuses (string values are identical across both enums)
  [AuditStatus.IN_PROGRESS]: BadgeType.Warning,
  [AuditStatus.COMPLETED]: BadgeType.Positive,
  // Cancelled is a terminal-negative state (the work will not be
  // completed) — desktop paints it red, so V2 mobile matches.
  [AuditStatus.CANCELLED]: BadgeType.Negative,
  // Audit-only statuses
  [AuditStatus.EXPIRED]: BadgeType.Negative,
  [AuditStatus.NOT_STARTED]: BadgeType.Neutral,
  [AuditStatus.SCHEDULED]: BadgeType.Neutral,
  // Task-only statuses
  [TaskStatus.DRAFT]: BadgeType.Neutral,
  [TaskStatus.ASSIGNED]: BadgeType.Neutral,
  [TaskStatus.OVERDUE]: BadgeType.Negative,
};

/** Status-fill token paths used for card left borders. Typed as a union of
 * valid token() paths so no casting is needed at the call site. */
type StatusBorderToken =
  | 'color.status.warning-fill'
  | 'color.status.success-fill'
  | 'color.status.neutral-fill'
  | 'color.status.critical-fill'
  | 'color.status.info-fill';

export const STATUS_BORDER_TOKEN: Record<string, StatusBorderToken> = {
  // Shared audit + task statuses
  [AuditStatus.IN_PROGRESS]: 'color.status.warning-fill',
  [AuditStatus.COMPLETED]: 'color.status.success-fill',
  // Cancelled uses the critical border to match its Negative badge —
  // Cancelled and Expired are visually paired (both dead-end states).
  [AuditStatus.CANCELLED]: 'color.status.critical-fill',
  // Audit-only statuses
  [AuditStatus.EXPIRED]: 'color.status.critical-fill',
  [AuditStatus.NOT_STARTED]: 'color.status.neutral-fill',
  [AuditStatus.SCHEDULED]: 'color.status.neutral-fill',
  // Task-only statuses
  [TaskStatus.DRAFT]: 'color.status.neutral-fill',
  [TaskStatus.ASSIGNED]: 'color.status.neutral-fill',
  [TaskStatus.OVERDUE]: 'color.status.critical-fill',
};

export const STATUS_DISPLAY_LABEL: Record<string, string> = {
  // Shared audit + task statuses
  [AuditStatus.IN_PROGRESS]: 'In Progress',
  [AuditStatus.COMPLETED]: 'Completed',
  [AuditStatus.CANCELLED]: 'Cancelled',
  // Audit-only statuses
  [AuditStatus.EXPIRED]: 'Expired',
  [AuditStatus.NOT_STARTED]: 'Not Started',
  [AuditStatus.SCHEDULED]: 'Scheduled',
  // Task-only statuses
  [TaskStatus.DRAFT]: 'Draft',
  [TaskStatus.ASSIGNED]: 'Assigned',
  [TaskStatus.OVERDUE]: 'Overdue',
};

/**
 * Human-readable labels for the raw `AuditType` enum values returned by
 * the SDK. Rendering `audit.auditType` directly shows the machine
 * spelling (e.g. `SITUATION`, `MPA`), which is not the UX we ship on
 * either V1 desktop or the audit list filter chips — both go through
 * a label map. Mirrors `STATUS_DISPLAY_LABEL` in shape so the pattern
 * is consistent, and the callers use the same `? ?? raw` fallback for
 * an unrecognised enum value (visible bug signal rather than a crash).
 *
 * `SITUATION` intentionally maps to `Situational` to match the audit
 * list filter chip translation (`auditTypeSituational`), rather than
 * V1's older `Situation` spelling — one label per surface, decided in
 * one place.
 */
export const AUDIT_TYPE_DISPLAY_LABEL: Record<string, string> = {
  [AuditType.PROGRESS]: 'Progress',
  [AuditType.MPA]: 'MPA',
  [AuditType.CALIBRATION]: 'Calibration',
  [AuditType.TEST]: 'Test',
  [AuditType.SITUATION]: 'Situational',
};

// ─── Reusable Styles ─────────────────────────────────────────────────────────

// No Stencil token exists for border widths in this package (searched:
// no @amzn/stencil-* borderWidth token surface). This constant is
// pre-existing (commit 6af95d86, unchanged by this CR) — modifying it
// would be scope creep with visual-regression risk on every ListCard
// consumer.
const STATUS_BORDER_WIDTH = '4px'; // standards-ignore: no Stencil border-width token; pre-existing constant

/** Tappable card base styles — reuse for any clickable card with a status border. */
export const TAPPABLE_CARD_STYLE = (
  borderToken: StatusBorderToken,
): { borderLeft: string; cursor: 'pointer' } => {
  const borderColor = token(borderToken) ?? '';
  return {
    borderLeft: `${STATUS_BORDER_WIDTH} solid ${borderColor}`,
    cursor: 'pointer' as const,
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

interface ListCardProps {
  /** Status string (e.g. "IN_PROGRESS", "COMPLETED") — drives border color. */
  status: string;
  /** Called when the card is tapped/clicked. */
  onTap: () => void;
  /** Stable Stencil test ID for integration-test identity assertions. */
  dataTestId?: string;
  /** Card content — rendered inside a vertical flex column. */
  children: React.ReactNode;
}

/**
 * Shared tappable list card with status-colored left border.
 * Used by audit list, task list, and any future entity list.
 *
 * Content is domain-specific — pass it as children.
 * Status mappings (badge type, border color, label) are exported
 * so feature components can render badges with consistent styling.
 *
 * @example
 * ```tsx
 * <ListCard status={task.taskStatus} onTap={() => navigate(...)}>
 *   <H3>{task.taskName}</H3>
 *   <Badge type={STATUS_BADGE_TYPE[task.taskStatus]} value={STATUS_DISPLAY_LABEL[task.taskStatus]} />
 * </ListCard>
 * ```
 */
export function ListCard({
  status,
  onTap,
  dataTestId,
  children,
}: ListCardProps): JSX.Element {
  // eslint-disable-next-line security/detect-object-injection -- status comes from typed SDK enums, not user input
  const borderToken = STATUS_BORDER_TOKEN[status] ?? 'color.status.neutral-fill';

  return (
    <Card
      padding="dimensions.spacing.300"
      css={TAPPABLE_CARD_STYLE(borderToken)}
      onClick={onTap}
      {...(dataTestId ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.200" width="100%">
        {children}
      </Col>
    </Card>
  );
}
